import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'wouter';
import {
  Plane,
  CircleDollarSign,
  Trophy,
  Clock,
  AlertTriangle,
  Shield,
  Edit3,
  Check,
  XCircle,
  Plus,
} from 'lucide-react';
import { CustomerConversation } from '@/components/customer-conversation';
import {
  api,
  type CustomerScorecardResponse,
  type PatchCustomerResponse,
  type PatchContactResponse,
  type CreateContactResponse,
  type CustomerContact,
} from '@/lib/api';
import { SourcingChannelChips } from './sourcing-channel-chips';
import { CopyButton } from './copy-button';
import { DocumentLink } from './document-link';

interface Props {
  publicId: string;
}

function fmtMoney(cents: number, currency = 'USD'): string {
  if (cents === 0) return '—';
  if (currency === 'JPY') return `¥${Math.round(cents / 100).toLocaleString('en-US')}`;
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

export function CustomerDetail({ publicId }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['customer', publicId],
    queryFn: () =>
      api<CustomerScorecardResponse>(`/api/customers/${publicId}`, {
        tokenGetter: () => getToken(),
      }),
    staleTime: 10_000,
  });

  const [editing, setEditing] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [draft, setDraft] = useState<{
    legalName: string;
    displayName: string;
    countryCode: string;
    isFaaPart145: boolean;
    faaCertNumber: string;
    isMilitary: boolean;
    carrierCertNumber: string;
    markupPctOverride: string;
  } | null>(null);

  const patchCustomer = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('No draft');
      const body: Record<string, unknown> = {};
      if (draft.legalName !== data?.customer.legalName) body.legalName = draft.legalName;
      if (draft.displayName !== (data?.customer.displayName ?? '')) {
        body.displayName = draft.displayName || null;
      }
      if (draft.countryCode !== (data?.customer.countryCode ?? '')) {
        body.countryCode = draft.countryCode || null;
      }
      if (draft.isFaaPart145 !== data?.customer.isFaaPart145) body.isFaaPart145 = draft.isFaaPart145;
      if (draft.faaCertNumber !== (data?.customer.faaCertNumber ?? '')) {
        body.faaCertNumber = draft.faaCertNumber || null;
      }
      if (draft.isMilitary !== data?.customer.isMilitary) body.isMilitary = draft.isMilitary;
      if (draft.carrierCertNumber !== (data?.customer.carrierCertNumber ?? '')) {
        body.carrierCertNumber = draft.carrierCertNumber || null;
      }
      const newMarkup = draft.markupPctOverride === '' ? null : Number(draft.markupPctOverride);
      const oldMarkup = data?.customer.markupPctOverride ? Number(data.customer.markupPctOverride) : null;
      if (newMarkup !== oldMarkup) body.markupPctOverride = newMarkup;

      return api<PatchCustomerResponse>(`/api/customers/${publicId}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer', publicId] });
      qc.invalidateQueries({ queryKey: ['customers'] });
      setEditing(false);
      setDraft(null);
    },
  });

  function startEdit() {
    if (!data) return;
    setDraft({
      legalName: data.customer.legalName,
      displayName: data.customer.displayName ?? '',
      countryCode: data.customer.countryCode ?? '',
      isFaaPart145: data.customer.isFaaPart145,
      faaCertNumber: data.customer.faaCertNumber ?? '',
      isMilitary: data.customer.isMilitary,
      carrierCertNumber: data.customer.carrierCertNumber ?? '',
      markupPctOverride: data.customer.markupPctOverride ?? '',
    });
    setEditing(true);
  }

  if (isLoading) return <div className="p-6 text-sm text-foreground-muted">Loading…</div>;
  if (isError) {
    return (
      <div className="p-6 text-sm text-danger">
        {(error as { message?: string })?.message ?? 'Failed to load customer'}
      </div>
    );
  }
  if (!data) return null;

  const { customer, kpis, topPartNumbers, topAircraftPlatforms, contacts: customerContacts, recentActivity } = data;

  return (
    <div className="h-full overflow-auto">
      {/* Header */}
      <div className="px-6 py-5 border-b border-border">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate">
              {customer.displayName ?? customer.legalName}
            </div>
            {customer.displayName && customer.displayName !== customer.legalName && (
              <div className="text-xs text-foreground-muted">{customer.legalName}</div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {customer.isFaaPart145 && <span className="pill pill-info">FAA Part 145</span>}
            {customer.isMilitary && <span className="pill pill-default">Military</span>}
            {customer.countryCode && <span className="pill pill-default">{customer.countryCode}</span>}
            {customer.source === 'chatbot' && <span className="pill pill-info">via chatbot</span>}
            {customer.markupPctOverride && (
              <span className="pill pill-warning">markup {customer.markupPctOverride}%</span>
            )}
            {!editing && (
              <button onClick={startEdit} className="btn-ghost text-xs">
                <Edit3 className="w-3.5 h-3.5" /> Edit
              </button>
            )}
          </div>
        </div>

        {customer.emailDomain && (
          <div className="mt-2 text-xs text-foreground-muted">
            <span className="font-mono">{customer.emailDomain}</span>
            {customer.ghlContactId && (
              <span className="ml-2">· GHL contact: <span className="font-mono">{customer.ghlContactId}</span></span>
            )}
          </div>
        )}

        {/* Lead notes — captures the chatbot conversation (and any other
            free-text broker context) so no-RFQ leads aren't lost. */}
        {customer.notes && (
          <div className="mt-3 rounded-md border border-border bg-surface-muted/40 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wide font-semibold text-foreground-muted mb-1">
              Lead notes
            </div>
            <div className="text-xs text-foreground whitespace-pre-wrap break-words max-h-48 overflow-auto">
              {customer.notes}
            </div>
          </div>
        )}

        {/* KPI tiles */}
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiTile
            icon={<Plane className="w-4 h-4" />}
            label="Lifetime RFQs"
            value={String(kpis.lifetimeRfqs)}
            sub={kpis.rfqs90d > 0 ? `${kpis.rfqs90d} in 90d` : undefined}
            tone="primary"
          />
          <KpiTile
            icon={<CircleDollarSign className="w-4 h-4" />}
            label="Lifetime paid"
            value={fmtMoney(kpis.lifetimePaidCents)}
            sub={`${kpis.lifetimePaidCount} invoice${kpis.lifetimePaidCount === 1 ? '' : 's'}`}
            tone="success"
          />
          <KpiTile
            icon={<Trophy className="w-4 h-4" />}
            label="Win rate"
            value={kpis.winRate == null ? '—' : `${kpis.winRate.toFixed(0)}%`}
            sub={
              kpis.winRateResolved > 0
                ? `${kpis.winRateAccepted} of ${kpis.winRateResolved} quotes`
                : 'no quotes resolved'
            }
            tone="warning"
          />
          <KpiTile
            icon={<Clock className="w-4 h-4" />}
            label="Avg response"
            value={kpis.avgResponseHours == null ? '—' : `${kpis.avgResponseHours.toFixed(1)}h`}
            sub="RFQ → quote sent"
            tone="info"
          />
        </div>

        {/* Secondary KPI strip */}
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-foreground-muted">
          {kpis.firstRfqAt && (
            <span>
              First RFQ: <span className="text-foreground">{new Date(kpis.firstRfqAt).toLocaleDateString()}</span>
            </span>
          )}
          {kpis.lastRfqAt && (
            <span>
              Last RFQ: <span className="text-foreground">{new Date(kpis.lastRfqAt).toLocaleDateString()}</span>
            </span>
          )}
          {kpis.avgOrderCents != null && (
            <span>
              Avg order: <span className="text-foreground font-mono">{fmtMoney(kpis.avgOrderCents)}</span>
            </span>
          )}
          {kpis.aogCount > 0 && (
            <span className="text-warning flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              <span className="font-medium">{kpis.aogCount} AOG line{kpis.aogCount === 1 ? '' : 's'}</span>
              {kpis.lastAogAt && <span>· last {new Date(kpis.lastAogAt).toLocaleDateString()}</span>}
            </span>
          )}
        </div>
      </div>

      {/* Edit form */}
      {editing && draft && (
        <div className="px-6 py-4 bg-surface-muted/40 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-wide font-semibold text-foreground-muted">
              Edit customer
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => { setEditing(false); setDraft(null); }}
                className="btn-ghost text-xs"
              >
                <XCircle className="w-3.5 h-3.5" /> Cancel
              </button>
              <button
                onClick={() => patchCustomer.mutate()}
                disabled={patchCustomer.isPending}
                className="btn-primary text-xs"
              >
                <Check className="w-3.5 h-3.5" />
                {patchCustomer.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Legal name">
              <input
                value={draft.legalName}
                onChange={(e) => setDraft({ ...draft, legalName: e.target.value })}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                maxLength={200}
              />
            </Field>
            <Field label="Display name (optional)">
              <input
                value={draft.displayName}
                onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                maxLength={200}
                placeholder="defaults to legal name"
              />
            </Field>
            <Field label="Country code (ISO 2)">
              <input
                value={draft.countryCode}
                onChange={(e) => setDraft({ ...draft, countryCode: e.target.value.toUpperCase().slice(0, 2) })}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                maxLength={2}
                placeholder="US, GB, FR…"
              />
            </Field>
            <Field label="Markup % override (blank = use tenant default)">
              <input
                type="number"
                step="0.01"
                min={0}
                max={500}
                value={draft.markupPctOverride}
                onChange={(e) => setDraft({ ...draft, markupPctOverride: e.target.value })}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                placeholder="e.g. 12.5"
              />
            </Field>
            <Field label="FAA Part 145 cert number (optional)">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.isFaaPart145}
                  onChange={(e) => setDraft({ ...draft, isFaaPart145: e.target.checked })}
                />
                <input
                  value={draft.faaCertNumber}
                  onChange={(e) => setDraft({ ...draft, faaCertNumber: e.target.value })}
                  disabled={!draft.isFaaPart145}
                  className="flex-1 px-2 py-1.5 text-sm bg-background border border-border rounded font-mono disabled:opacity-50"
                  maxLength={80}
                  placeholder="cert #"
                />
              </div>
            </Field>
            <Field label="Military / carrier cert (W.S. §39-15-105 exemption)">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.isMilitary}
                  onChange={(e) => setDraft({ ...draft, isMilitary: e.target.checked })}
                />
                <input
                  value={draft.carrierCertNumber}
                  onChange={(e) => setDraft({ ...draft, carrierCertNumber: e.target.value })}
                  disabled={!draft.isMilitary}
                  className="flex-1 px-2 py-1.5 text-sm bg-background border border-border rounded font-mono disabled:opacity-50"
                  maxLength={80}
                  placeholder="carrier cert #"
                />
              </div>
            </Field>
          </div>
          {patchCustomer.isError && (
            <div className="mt-3 text-sm text-danger">
              {(patchCustomer.error as { message?: string })?.message ?? 'Save failed'}
            </div>
          )}
        </div>
      )}

      {/* Top PNs + Top platforms */}
      <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        <section>
          <h3 className="eyebrow mb-2">Top part numbers requested</h3>
          {topPartNumbers.length === 0 ? (
            <div className="text-xs text-foreground-muted italic">No data yet.</div>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {topPartNumbers.map((p) => (
                <li
                  key={p.partNumber}
                  className="text-xs px-2 py-1 rounded bg-muted flex items-baseline gap-1.5"
                >
                  <span className="font-mono">{p.partNumber}</span>
                  <span className="text-foreground-muted">×{p.rfqCount}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h3 className="eyebrow mb-2">Top aircraft platforms</h3>
          {topAircraftPlatforms.length === 0 ? (
            <div className="text-xs text-foreground-muted italic">No platform data on lines.</div>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {topAircraftPlatforms.map((p) => (
                <li
                  key={p.platform}
                  className="text-xs px-2 py-1 rounded bg-primary-muted text-primary-strong flex items-baseline gap-1.5"
                >
                  <span className="font-mono font-medium">{p.platform}</span>
                  <span className="opacity-70">×{p.rfqCount}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Contacts */}
      <div className="px-6 py-4 border-t border-border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="eyebrow">Contacts ({customerContacts.length})</h3>
          {!showAddContact && (
            <button onClick={() => setShowAddContact(true)} className="btn-ghost text-xs">
              <Plus className="w-3.5 h-3.5" /> Add contact
            </button>
          )}
        </div>
        {showAddContact && (
          <AddContactForm
            customerPublicId={publicId}
            onClose={() => setShowAddContact(false)}
          />
        )}
        {customerContacts.length === 0 ? (
          !showAddContact && (
            <div className="text-xs italic text-foreground-muted">
              No contacts. They auto-populate from the email field on each RFQ — or add one manually.
            </div>
          )
        ) : (
          <ul className="space-y-1.5">
            {customerContacts.map((c) => (
              <ContactRow key={c.id} contact={c} customerPublicId={publicId} />
            ))}
          </ul>
        )}
      </div>

      {/* Conversation (GHL) — read-only, lazy-loaded */}
      <div className="px-6 py-4 border-t border-border">
        <CustomerConversation publicId={publicId} />
      </div>

      {/* Recent activity */}
      <div className="px-6 py-4 border-t border-border">
        <h3 className="eyebrow mb-2">Recent RFQs ({recentActivity.length})</h3>
        {recentActivity.length === 0 ? (
          <div className="text-xs italic text-foreground-muted">No RFQ history.</div>
        ) : (
          <ul className="divide-y divide-border">
            {recentActivity.map((r) => (
              <li key={r.rfqPublicId} className="py-2">
                <Link
                  href={`/rfqs/${r.rfqPublicId}`}
                  className="block hover:bg-surface-muted -mx-2 px-2 py-1 rounded transition-colors"
                >
                  <div className="flex items-baseline justify-between gap-2 flex-wrap text-xs">
                    <div className="flex items-baseline gap-2 min-w-0 flex-wrap">
                      <span className="font-mono font-medium">{r.rfqRef}</span>
                      {r.isAog && <span className="pill pill-danger text-[10px]">AOG</span>}
                      {r.isUrgent && !r.isAog && <span className="pill text-[10px] bg-amber-500 text-white">URGENT</span>}
                      {r.sourceChannel && (
                        <span className="pill pill-default text-[10px]">{r.sourceChannel}</span>
                      )}
                      <StageBadges counts={r.stageCounts} totalLines={r.lineCount} />
                      <SourcingChannelChips
                        postedChannels={r.sourcingChannels}
                        variant="readonly"
                        hideUnposted
                      />
                    </div>
                    <div className="text-foreground-muted whitespace-nowrap">
                      {new Date(r.receivedAt).toLocaleDateString()}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* BIS screening status */}
      {(customer.bisLastScreenedAt || customer.bisScreenStatus) && (
        <div className="px-6 py-4 border-t border-border">
          <h3 className="eyebrow mb-2 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" />
            BIS screening
          </h3>
          <div className="text-xs text-foreground-muted">
            Status: <span className="font-medium text-foreground">{customer.bisScreenStatus ?? 'not screened'}</span>
            {customer.bisLastScreenedAt && (
              <span className="ml-2">
                · last screened {new Date(customer.bisLastScreenedAt).toLocaleDateString()}
              </span>
            )}
            {(customer.bisScreenPdfKey || customer.bisScreenPdfUrl) && (
              <DocumentLink
                objectKey={customer.bisScreenPdfKey}
                fallbackUrl={customer.bisScreenPdfUrl}
                label="View report"
                filename={customer.bisScreenDocumentFilename}
                className="ml-2 text-primary hover:underline"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KpiTile + Field helpers
// ---------------------------------------------------------------------------

const TONE: Record<'primary' | 'success' | 'info' | 'warning', { iconBg: string; iconText: string }> = {
  primary: { iconBg: 'bg-primary-muted', iconText: 'text-primary-strong' },
  success: { iconBg: 'bg-success-muted', iconText: 'text-success' },
  info:    { iconBg: 'bg-info-muted',    iconText: 'text-primary-strong' },
  warning: { iconBg: 'bg-warning-muted', iconText: 'text-warning' },
};

function KpiTile({
  icon,
  label,
  value,
  sub,
  tone = 'primary',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tone?: keyof typeof TONE;
}) {
  const t = TONE[tone];
  return (
    <div className="card p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-foreground-muted">{label}</div>
          <div className="text-xl font-bold tabular-nums tracking-tight mt-1">{value}</div>
          {sub && <div className="text-[10px] text-foreground-muted mt-0.5">{sub}</div>}
        </div>
        <div className={`w-7 h-7 rounded ${t.iconBg} ${t.iconText} flex items-center justify-center flex-shrink-0`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-[11px] font-medium text-foreground-muted mb-1">{label}</div>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// StageBadges — compact roll-up of line-item stages per RFQ.
//
// Shows up to 3 most-relevant stage counts as pills (e.g. "1 paid · 2 sourcing").
// Color-coded so the broker scans at a glance:
//   - paid/delivered/reviewed → success (green)
//   - shipped/invoiced/po_sent/accepted → primary (blue)
//   - quoted → info (light blue)
//   - sourcing/received → muted (gray)
//   - lost/no_quote → danger (red, condensed to "lost N")
// ---------------------------------------------------------------------------

const STAGE_TONE: Record<string, string> = {
  received: 'pill-default',
  sourcing: 'pill-default',
  quoted: 'pill-info',
  accepted: 'pill-primary',
  po_sent: 'pill-primary',
  invoiced: 'pill-primary',
  paid: 'pill-success',
  shipped: 'pill-primary',
  delivered: 'pill-success',
  reviewed: 'pill-success',
  no_quote: 'pill-danger',
  lost: 'pill-danger',
};

const STAGE_LABEL: Record<string, string> = {
  received: 'received',
  sourcing: 'sourcing',
  quoted: 'quoted',
  accepted: 'accepted',
  po_sent: 'PO sent',
  invoiced: 'invoiced',
  paid: 'paid',
  shipped: 'shipped',
  delivered: 'delivered',
  reviewed: 'reviewed',
  no_quote: 'no quote',
  lost: 'lost',
};

function StageBadges({
  counts,
  totalLines,
}: {
  counts: Partial<Record<string, number>>;
  totalLines: number;
}) {
  const entries = Object.entries(counts).filter(([, n]) => (n ?? 0) > 0) as Array<[string, number]>;
  if (entries.length === 0) {
    return <span className="text-foreground-muted text-[10px]">{totalLines} line{totalLines === 1 ? '' : 's'}</span>;
  }
  return (
    <span className="flex items-center gap-1 flex-wrap">
      {entries.map(([stage, n]) => (
        <span key={stage} className={`pill ${STAGE_TONE[stage] ?? 'pill-default'} text-[10px]`}>
          {n} {STAGE_LABEL[stage] ?? stage}
        </span>
      ))}
    </span>
  );
}

// A customer contact row with inline editing (name / title / email / phone).
// Saves via PATCH /api/contacts/:id, then refreshes the customer scorecard.
function ContactRow({ contact, customerPublicId }: { contact: CustomerContact; customerPublicId: string }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ fullName: '', email: '', phone: '', jobTitle: '' });
  const [err, setErr] = useState<string | null>(null);

  function startEdit() {
    setDraft({
      fullName: contact.fullName ?? '',
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      jobTitle: contact.jobTitle ?? '',
    });
    setErr(null);
    setEditing(true);
  }

  const save = useMutation({
    mutationFn: () =>
      api<PatchContactResponse>(`/api/contacts/${contact.id}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify({
          fullName: draft.fullName.trim() || null,
          email: draft.email.trim(),
          phone: draft.phone.trim() || null,
          jobTitle: draft.jobTitle.trim() || null,
        }),
      }),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['customer', customerPublicId] });
    },
    onError: (e: { message?: string }) => setErr(e?.message ?? 'Save failed'),
  });

  if (editing) {
    return (
      <li className="text-xs border border-border rounded p-2.5 bg-surface space-y-1.5">
        <div className="grid grid-cols-2 gap-1.5">
          <input
            value={draft.fullName}
            onChange={(e) => setDraft((d) => ({ ...d, fullName: e.target.value }))}
            placeholder="Full name"
            className="px-2 py-1 border border-border rounded bg-background"
          />
          <input
            value={draft.jobTitle}
            onChange={(e) => setDraft((d) => ({ ...d, jobTitle: e.target.value }))}
            placeholder="Job title"
            className="px-2 py-1 border border-border rounded bg-background"
          />
          <input
            value={draft.email}
            onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
            placeholder="Email"
            type="email"
            className="px-2 py-1 border border-border rounded bg-background"
          />
          <input
            value={draft.phone}
            onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
            placeholder="Phone"
            className="px-2 py-1 border border-border rounded bg-background"
          />
        </div>
        {err && <div className="text-destructive">{err}</div>}
        <div className="flex gap-2">
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending || !draft.email.trim()}
            className="btn-primary text-xs"
          >
            <Check className="w-3 h-3" /> {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => { setEditing(false); setErr(null); }} className="btn-ghost text-xs">
            <XCircle className="w-3 h-3" /> Cancel
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="text-xs border border-border rounded p-2.5 bg-surface flex items-baseline justify-between gap-2 flex-wrap">
      <div className="min-w-0">
        <div className="font-medium">
          {contact.fullName ?? <span className="italic text-foreground-muted">unnamed</span>}
          {contact.jobTitle && <span className="ml-1.5 text-foreground-muted">· {contact.jobTitle}</span>}
        </div>
        <div className="font-mono text-foreground-muted flex items-center gap-1 flex-wrap">
          <span className="truncate">{contact.email}</span>
          <CopyButton value={contact.email} label={`Copy ${contact.email}`} size="xs" />
          {contact.phone && (
            <>
              <span>· {contact.phone}</span>
              <CopyButton value={contact.phone} label={`Copy ${contact.phone}`} size="xs" />
            </>
          )}
        </div>
      </div>
      <button onClick={startEdit} className="btn-ghost text-[11px] px-1.5 py-0.5 flex-shrink-0">
        <Edit3 className="w-3 h-3" /> Edit
      </button>
    </li>
  );
}

// Inline "add a person to this customer" form. POSTs to /api/contacts with the
// customer's publicId, then refreshes the scorecard. Email is required (it's the
// dedup key); name/title/phone are optional.
function AddContactForm({
  customerPublicId,
  onClose,
}: {
  customerPublicId: string;
  onClose: () => void;
}) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [draft, setDraft] = useState({ fullName: '', email: '', phone: '', jobTitle: '' });
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api<CreateContactResponse>('/api/contacts', {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify({
          customerPublicId,
          email: draft.email.trim(),
          fullName: draft.fullName.trim() || undefined,
          phone: draft.phone.trim() || undefined,
          jobTitle: draft.jobTitle.trim() || undefined,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer', customerPublicId] });
      onClose();
    },
    onError: (e: { message?: string }) => setErr(e?.message ?? 'Could not add contact'),
  });

  return (
    <div className="text-xs border border-border rounded p-2.5 bg-surface-muted/40 space-y-1.5 mb-2">
      <div className="grid grid-cols-2 gap-1.5">
        <input
          value={draft.fullName}
          onChange={(e) => setDraft((d) => ({ ...d, fullName: e.target.value }))}
          placeholder="Full name"
          className="px-2 py-1 border border-border rounded bg-background"
        />
        <input
          value={draft.jobTitle}
          onChange={(e) => setDraft((d) => ({ ...d, jobTitle: e.target.value }))}
          placeholder="Job title (e.g. CEO)"
          className="px-2 py-1 border border-border rounded bg-background"
        />
        <input
          value={draft.email}
          onChange={(e) => { setDraft((d) => ({ ...d, email: e.target.value })); setErr(null); }}
          placeholder="Email (required)"
          type="email"
          className="px-2 py-1 border border-border rounded bg-background"
        />
        <input
          value={draft.phone}
          onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
          placeholder="Phone"
          className="px-2 py-1 border border-border rounded bg-background"
        />
      </div>
      {err && <div className="text-danger">{err}</div>}
      <div className="flex gap-2">
        <button
          onClick={() => create.mutate()}
          disabled={create.isPending || !draft.email.trim()}
          className="btn-primary text-xs"
        >
          <Check className="w-3 h-3" /> {create.isPending ? 'Adding…' : 'Add contact'}
        </button>
        <button onClick={() => { onClose(); setErr(null); }} className="btn-ghost text-xs">
          <XCircle className="w-3 h-3" /> Cancel
        </button>
      </div>
    </div>
  );
}

